/**
 * Program: Router
 * Author: SDL
 * Description: router setting file
 */
import Vue from 'vue';
import Router from 'vue-router';
import _ from 'lodash';
import axios from 'axios';
import store from '@/vuex/store';
import { fetchMenuAll } from '@/service/menuService';
import { SDLUtil, MFAUtil } from '@/utils';

import { i18n, loadLanguageAsync } from '../i18n';
// 일반
import LoginPage from '@/components/view/admin/main/LoginPage';
import MainPage from '@/components/view/admin/main/MainPage';
import MainIndexPage from '@/components/view/admin/main/MainIndexPage';
import EmptyPage from '@/components/view/admin/main/EmptyPage';
import { signupService, syslinkService } from '@/service';
import loginService from "@/service/loginService";
import maintenance from "@/components/view/admin/maintenance/Maintenance.vue";
import moment from 'moment-timezone';
import MaintenanceAlertPopup from "@/components/common/popup/MaintenanceAlertPopup.vue";

const redirectParamMapping = (query) => {
  const redirect = query.redirect;
  if (redirect && redirect !== '/') {
    const routerObj = {};
    if (redirect.indexOf("?") > -1) {
      const queryObj = {};
      const queries = redirect.substring(redirect.indexOf("?") + 1, redirect.length);
      const queriesArray  = queries.split("&");
      queriesArray.forEach((query) => {
        const [key, ...values] = query.split('=');
        queryObj[key] = decodeURIComponent(values.join('='));
      });
      routerObj.query = queryObj;
      routerObj.path = redirect.substring(0, redirect.indexOf("?"));
    }else {
      routerObj.path = redirect;
    }
    return routerObj;
  }
  return null;
}

Vue.use(Router);

export const router = new Router({
  mode: 'history',
  routes: [
    // login
    {
      name: 'login',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/login`,
      component: LoginPage,
    },
    // logout
    {
      name: 'logout',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/logout`,
      component: () => import(/* webpackChunkName: '[request]' */ '@/components/view/admin/main/LoginOut'),
    },
    // system registration
    {
      name: 'systemUserRegister',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/register`,
      component: () => import(/* webpackChunkName: '[request]' */ '@/components/view/admin/main/RegisterPage'),
    },
    // system subscription clause
    {
      name: 'terms',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/terms-conditions`,
      component: () => import(/* webpackChunkName: '[request]' */ '@/components/view/admin/main/TermsAndConditionsPage'),
    },
    // Password initialization
    {
      name: 'initPassword',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/initPassword`,
      component: () => import(/* webpackChunkName: '[request]' */ '@/components/view/admin/main/InitPassword'),
    },
    // sample test
    {
      path: `${SDLUtil.WEB_CONTEXT_PATH}/sample/tree`,
      component: () => import('@/components/view/sample/Tree'),
    },
    {
      path: `${SDLUtil.WEB_CONTEXT_PATH}/sample/fileuploader`,
      component: () => import('@/components/view/sample/FileUploader'),
    },
    {
      path: `${SDLUtil.WEB_CONTEXT_PATH}/sample/tabs`,
      component: () => import('@/components/view/sample/Tabs'),
    },
    {
      path: `${SDLUtil.WEB_CONTEXT_PATH}/sample/excel`,
      component: () => import('@/components/view/sample/Excel'),
    },
    {
      path: `${SDLUtil.WEB_CONTEXT_PATH}/sample/regexinput`,
      component: () => import('@/components/view/sample/RegexInput'),
    },
    // popup
    {
      name: 'noticePopup',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/noticePopup/:boardId/:postId`,
      component: () => import(/* webpackChunkName: '[request]' */ '@/components/view/admin/board/PostPopup'),
    },
    // popup
    {
      name: 'transPopup',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/transPopup`,
      component: () => import('@/components/view/admin/main/TransPopup'),
    },
    // popup (authorize)
    // {
    //   name: 'azureGetKeyPopup',
    //   path: `${SDLUtil.WEB_CONTEXT_PATH}/AzureGetKeyPopup`,
    //   component: () => import('@/components/view/coa/roles/popup/AzureGetKeyPopup'),
    // },
    // {
    //   name: 'gcpGetKeyPopup',
    //   path: `${SDLUtil.WEB_CONTEXT_PATH}/GCPGetKeyPopup`,
    //   component: () => import('@/components/view/coa/roles/popup/GCPGetKeyPopup'),
    // },
    // SCOP user manual
    {
      name: 'userManual',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/manual`,
      component: () => import('@/components/view/admin/main/MainManualPage'),
    },
    {
      name: 'mfaResponser',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/mfa/responser`,
      redirect: to => {
        if (to.query.redirect === 'popup') {
          MFAUtil.setMFAToken(to.query.jwtTokenResponse);
          return window.close();
        }
        const mfaToken = SDLUtil.parseJwt(to.query.jwtTokenResponse);
        if (!mfaToken) return SDLUtil.alert({msg: 'Invalid MFA Token', onOkEvt: () => {path: `${SDLUtil.WEB_CONTEXT_PATH}/logout`} } );
        if (mfaToken.ret !== '1') return SDLUtil.alert({ msg: mfaToken.msg, onOkEvt: () => {path: `${SDLUtil.WEB_CONTEXT_PATH}/logout`} });
        let redirection = redirectParamMapping(to.query);
        if(!redirection) redirection = to.query.redirect;
        axios
          .post(`${SDLUtil.API_URL}/mfa/tokens`)
          .then(({data}) => {
            loginService.setToken(data.authToken);
            loginService.setRefreshToken(data.refreshToken);
            router.replace(redirection);
          });
      },
    },
    // No page registration in menu management => Unconditional 404 processing of user criteria regardless of component registration
    {
      name: 'emptyPage',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/emptypage`,
      component: EmptyPage,
    },
    {
      name: '404',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/404`,
      component: EmptyPage,
    },
    {
      name: 'warning',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/warning`,
      component: EmptyPage,
    },
    // Page if not authenticated
    {
      name: 'noauth',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/noauth`,
      component: EmptyPage,
    },
    {
      name: 'main',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/`,
      alias: '/',
      component: MainPage,
      children: [
        {
          name: 'mainIndexPage',
          path: '',
          component: MainIndexPage,
          beforeEnter: async (to, from, next) => {
            if (!_.isEmpty(to.query.redirect)) {
              next({ path: to.query.redirect });
              return false;
            }
            next();
          }
        },
      ], // mock
    },
    // otherwise redirect to home
    {
      path: '*',
      redirect: `${SDLUtil.WEB_CONTEXT_PATH}/404`,
    },
    {
      name: 'userSignup',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/userSignup`,
      component: () => import(/* webpackChunkName: '[request]' */ '@/components/view/admin/main/UserSignup'),
    },
    {
      name: 'sysLinkLogin',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/sysLinkLogin`,
      component: () => import(/* webpackChunkName: '[request]' */ '@/components/view/sysLink/SysLinkLogin'),
    },
    {
      name: 'maintenance',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/maintenance`,
      component: maintenance
    }
  ],
});
router.beforeEach((to, from, next) => {
  // redirect to login page if not logged in and trying to access a restricted page
  const publicPages = ['/login', '/logout', '/register', '/initPassword', '/warning','/error'].map(
    page => SDLUtil.WEB_CONTEXT_PATH + page,
  );
  const authRequired = !publicPages.includes(to.path);
  const loggedIn = SDLUtil.isLogin();

  if (authRequired && !loggedIn) {
    if (to.fullPath.indexOf(`${SDLUtil.WEB_CONTEXT_PATH}/404`) > -1 || to.fullPath === `${SDLUtil.WEB_CONTEXT_PATH}/`) {
      return next(`${SDLUtil.WEB_CONTEXT_PATH}/login`);
    }
    if (to.fullPath.indexOf(`${SDLUtil.WEB_CONTEXT_PATH}/noauth`) > -1) {
      return next({ path: `${SDLUtil.WEB_CONTEXT_PATH}/login`, query: { redirect: to.redirectedFrom } });
    }
    return next({ path: `${SDLUtil.WEB_CONTEXT_PATH}/login`, query: { redirect: to.fullPath } });
  }
  return next();
});

router.beforeEach((to, from, next) => {
  // Import locale information from logged in user information
  let userPreferLang;
  const userInfo = SDLUtil.getLoginedUserInfo();
  if (userInfo && userInfo.language) {
    userPreferLang = userInfo.language;
  }

  const lang = (userPreferLang || window.navigator.userLanguage || window.navigator.language || SDLUtil.DEFAULT_LANG).replace(
    '-',
    '_',
  );
  loadLanguageAsync(lang).then(() => next());
});

router.beforeEach((to, from, next) => {
  const signupPublicPages = ['/logout', '/warning','error']
  if (SDLUtil.isLogin()) {
    signupService.isUserSignup()
      .then((isUserSignup) => {
        if (!isUserSignup
          && to.fullPath.indexOf('/userSignup') === -1
          && !signupPublicPages.includes(to.path)
          ) {
          let param = {
            path: `${SDLUtil.WEB_CONTEXT_PATH}/userSignup`,
            query: {},
          };
          if (to.query.redirectURI && syslinkService.isCorrectUrl(to.query.redirectURI)) param.query.redirect = `/sysLinkLogin?redirectURI=${to.query.redirectURI}`;
          else if (to.query.redirect && syslinkService.isCorrectUrl(to.query.redirect)) param.query.redirect = `${to.query.redirect}`;
          router.push(param);
        }
        else {
          return next();
        }
      })
      .catch(err => {
        console.error(err);
      })
  } else {
    return next();
  }
});

/**
* param router from, router to, router next
* return MFA Redirect
* exception
* description Page 이동시 MFA Check
* since 2023-05-18
*/
router.beforeEach((to, from, next) => {
  MFAUtil.getMFAAuthorizeByUrl(to, from, next);
});
// Add to menu ID, page ID header when changing router
router.beforeEach((to, from, next) => {
  if (to.meta.menuId) {
    axios.defaults.headers.common['menu-id'] = to.meta.menuId;
  } else {
    delete axios.defaults.headers.common['menu-id'];
  }
  if (to.meta.pageId) {
    axios.defaults.headers.common['page-id'] = to.meta.pageId;
  } else {
    delete axios.defaults.headers.common['page-id'];
  }

  return next();
});

// Language Settings When Changing Router
router.beforeEach((to, from, next) => {
  if (store.getters.getLocale !== '') {
    i18n.locale = store.getters.getLocale;
  }
  return next();
});

// Initialize storing the stored parameter setting value when changing the router
router.beforeEach((to, from, next) => {
  if (to.params.restoreList !== true && to.meta.menuId !== from.meta.menuId) {
    store.commit('PARAMS', {});
  }
  // console.info('파라미터값', store.getters.getParams, to.params.restoreList);
  return next();
});


router.afterEach(to => {
  let title = SDLUtil.getMsgProp('sdl.common.projectName');
  if (to.meta.labelJson && to.meta.labelJson[i18n.locale]) {
    title += ` - ${to.meta.labelJson[i18n.locale]}`;
  } else if (to.meta.label) {
    title += ` - ${to.meta.label}`;
  }
  document.title = title;
  if (to.meta.menuId !== undefined) {
    axios.post(`${SDLUtil.API_URL}/history/menuUseHistory/${to.meta.menuId}`).then();
    // axios.post(`${SDLUtil.API_URL}/history/menuUseHistory/${to.meta.menuId}`).then();
  }
});

export async function addEntryPoint(routeData = []) {
  const entryChildren = routeData
    .filter(menu => menu.pagePath)
    .map(menu => ({
      name: menu.pageId,
      path: menu.pagePath,
      redirect: `${SDLUtil.WEB_CONTEXT_PATH}/noauth`,
      meta: {
        ...menu,
      },
    }));
  const { routes } = router.options;
  const mainRoute = routes.find(r => r.name === 'main');

  // const existComponents = componentList.map(r => r.path);

  // If you are logged in
  if (SDLUtil.isLogin()) {
    // Import common code
    // await SDLUtil.loadAllCommCodeList();
    // await store.dispatch('getMenus');
    const termsUrl = `${SDLUtil.WEB_CONTEXT_PATH}/terms-conditions`;
    const isTermsPage = window.location.pathname === termsUrl;
    const userSignupUrl = `${SDLUtil.WEB_CONTEXT_PATH}/userSignup`;
    const isUserSignupPage = window.location.pathname === userSignupUrl;
    const menuResponse = await fetchMenuAll()
      .then(res => res)
      .catch(err => {
        // We need agreement.
        if (err.data.error.code === 201 && !isTermsPage) {
          console.info('change', err);
          window.location.href = termsUrl;
        }
      });

    // skip the terms and conditions page
    if (isTermsPage) {
      return;
    }

    await SDLUtil.loadAllCommCodeList();
    await store.dispatch('getMenus');
    //PM 공사중 페이지 관련 Interval
    await store.dispatch('getMaintenance');
    let isAlreadySeen = false;
    let count = 0;
    setInterval(() => {
      //1분마다 공사중 여부 확인
      if(!isAlreadySeen) {
        const maintenance = store.getters.getMaintenance;
        if(maintenance.regYn === 'Y') {
          const minuteDiff = moment(maintenance.fromPeriod).diff(moment(),'minute');
          if(minuteDiff < 11 && minuteDiff > -1) {
            isAlreadySeen = true;
            SDLUtil.show(MaintenanceAlertPopup, {}, { sizeClass: 'small' });
          }
        }
      }
      count++;
      //5분마다 maintenance 최신화
      if(count%5 === 0) {
        //로그아웃 10초 전부터 maintenance 갱신 해제
        if(SDLUtil.isLogin() && SDLUtil.getLoginedUserInfo().exp * 1000 > (new Date().getTime() + 10000)) store.dispatch('getMaintenance')
      }
    },60000);

    let authPages = [];
    menuResponse.data.forEach(menu => {
      const pages = menu.pages.map(page => {
        const routeItem = {
          name: page.pageId,
          path: page.pagePath,
          component: EmptyPage,
          meta: {
            ...menu,
            ...page,
          },
        };
        if (page.componentName !== undefined && page.componentName !== '') {
          routeItem.component = () => import(/* webpackChunkName: '[request]' */ `@/components/view/${page.componentName}`);
        }
        return routeItem;
      });

      authPages = authPages.concat(pages);
    });

    authPages.forEach(page => {
      const idx = _.findIndex(entryChildren, ['path', page.path]);
      if (idx > -1) {
        entryChildren[idx] = page;
      }
    });
  }

  mainRoute.children = [...entryChildren];

  router.addRoutes([mainRoute]);
}
const setMaintenance = async () => {
  await store.dispatch('getMenus');
};
router.onError(error => {
  if (/loading chunk \d* failed./i.test(error.message)) {
    window.location.reload();
  }
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js